import { listenForSmallScreen } from '$utils';
import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { ShowOnScreenSizeDirective } from '../../shared/directives/show-on-screen-size.directive';
import { UploadDirective } from '../../shared/directives/upload.directive';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { FileValidationError, FileValidationService } from './file-validation.service';

export interface FileData {
  fileId: number;
  fileName: string;
  file?: Blob;
  thumbnailUrl?: string;
  isMain?: boolean;
  savedFileSize?: number;
  category?: string;
  description?: string;
}

export interface AddNewFile {
  files: File[];
}

export interface MainFile {
  file_id: number;
  value?: boolean;
}

export interface RemoveFile {
  file_id: number;
}

@UntilDestroy()
@Component({
  selector: 'app-upload-files',
  templateUrl: './upload-files.component.html',
  styleUrls: ['./upload-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UploadDirective,
    ShowOnScreenSizeDirective,
    MessageModule,
    LoadingSpinnerComponent,
    TooltipModule,
    FormsModule,
    ReactiveFormsModule,
    NgStyle,
  ],
})
export class UploadFilesComponent implements OnInit {
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly fileValidationService = inject(FileValidationService);

  readonly files = input<FileData[] | null>(null);
  readonly maxFileSize = input<number>(1024);
  readonly allowedMimeTypes = input<string[]>();
  readonly supportedFilesLabel = input<string>('');
  readonly canSetMainFile = input<boolean>(false);
  readonly isLoading = input<boolean | null>(false);
  readonly categoryLabel = input<string | null | undefined>('');
  readonly editableFields = input<boolean>(false);
  readonly isDisabled = input<boolean>(false);
  readonly showFileName = input<boolean>(false);

  readonly onAdd = output<AddNewFile>();
  readonly onSetMain = output<MainFile>();
  readonly onRemove = output<RemoveFile>();
  readonly onRemoveAll = output<void>();

  readonly mainFileControl = new UntypedFormControl();
  readonly listenForSmallScreen = listenForSmallScreen;

  readonly fileSizeError = signal<FileValidationError | null>(null);
  readonly fileTypeError = signal<FileValidationError | null>(null);

  private lastSelectedFileId?: number;
  protected isDesktop = true;
  protected hasError = false;

  constructor() {
    this.setupScreenSizeListener();
    this.setupFilesChangeEffect();
  }

  ngOnInit(): void {
    this.setupMainFileChangeListener();
  }

  private setupScreenSizeListener(): void {
    this.listenForSmallScreen()
      .pipe(untilDestroyed(this))
      .subscribe(isDesktop => (this.isDesktop = isDesktop));
  }

  private setupFilesChangeEffect(): void {
    effect(() => {
      const currentFiles = this.files();
      if (currentFiles) {
        this.syncMainFileControl();
      }
    });
  }

  private setupMainFileChangeListener(): void {
    this.mainFileControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(fileId => this.handleMainFileChange(fileId));
  }

  handleDroppedFiles(fileList: FileList): void {
    const files = this.fileValidationService.convertFileListToArray(fileList);
    this.processFiles(files);
  }

  handleInputFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) {
      return;
    }

    const files = this.fileValidationService.convertFileListToArray(input.files);
    this.processFiles(files);
    input.value = '';
  }

  deleteFile(fileId: number): void {
    this.onRemove.emit({ file_id: fileId });
  }

  createThumbnail(file: Blob | null): SafeUrl | string {
    if (!file) {
      return '';
    }

    const objectUrl = URL.createObjectURL(file);
    return this.domSanitizer.bypassSecurityTrustUrl(objectUrl);
  }

  returnFileSize(sizeInBytes: number | null | undefined): string {
    return this.fileValidationService.formatFileSize(sizeInBytes);
  }

  truncateFilename(filename: string, maxLength: number): string {
    return this.fileValidationService.truncateFilename(filename, maxLength);
  }

  handleRemoveAll(): void {
    this.onRemoveAll.emit();
  }

  private processFiles(files: File[]): void {
    this.clearErrors();

    const validation = this.fileValidationService.validateFiles(
      files,
      this.maxFileSize(),
      this.allowedMimeTypes(),
      this.canSetMainFile(),
      this.isDesktop
    );

    if (!validation.isValid && validation.error) {
      this.setValidationError(validation.error);
      return;
    }

    this.emitFiles(files);
  }

  private clearErrors(): void {
    this.hasError = false;
    this.fileSizeError.set(null);
    this.fileTypeError.set(null);
  }

  private setValidationError(error: FileValidationError): void {
    this.hasError = true;

    if (error.summary.includes('file size')) {
      this.fileSizeError.set(error);
    } else if (error.summary.includes('file type')) {
      this.fileTypeError.set(error);
    }
  }

  private emitFiles(files: File[]): void {
    if (files.length > 0) {
      this.onAdd.emit({ files });
    }
  }

  private findMainFile(): FileData | null {
    const fileList = this.files();
    if (!fileList) {
      return null;
    }

    return fileList.find(file => file.isMain === true) || null;
  }

  private syncMainFileControl(): void {
    const mainFile = this.findMainFile();

    if (!mainFile || mainFile.fileId === this.lastSelectedFileId) {
      return;
    }

    this.lastSelectedFileId = mainFile.fileId;
    this.mainFileControl.patchValue(mainFile.fileId, { emitEvent: false });
  }

  private handleMainFileChange(fileId: number): void {
    if (fileId === this.lastSelectedFileId) {
      return;
    }

    this.lastSelectedFileId = fileId;
    this.onSetMain.emit({ file_id: fileId });
  }
}
